import { inject, injectable } from 'inversify';

import { FileUpload } from '../models/file-upload';
import { Logger } from '../models/logger';
import { GAContants } from '../types/ga.types';
import { ProcessorMetadataUtils } from '../utils/processor-metadata.utils';
import { GAService } from './analytics.service';
import { ApiConfig } from './api-config';
import { LightHttp } from './light-http.service';


@injectable()
export class FileDownloadService {
    private readonly logger = Logger.getLogger('FileDownloadService');

    constructor(
        @inject(GAService) private gaService: GAService,
        @inject(LightHttp) private http: LightHttp,
    ) {
    }

    emailDownload(fileInfo: FileUpload, email: string) {
        /*this.http.post(fileInfo.endpointData.domain + fileInfo.endpointData.emailDownload, {
            email: email
        }).subscribe(data => {
            console.log(data);
        });
        */
    }

    /** Only for file-list-items */
    getDirectDownloadLink(fileInfo: FileUpload, filename: string) {
        const fileDownloadDomain = fileInfo.endpointData?.processorMetadataList?.[0]?.downloadDomain;
        const downloadEndpoint = ProcessorMetadataUtils.getDownloadEndpoint(fileInfo.endpointData?.processorMetadataList?.[0]);
        if (!downloadEndpoint || !fileDownloadDomain) return null;
        const downloadUrl = downloadEndpoint +
            ((fileDownloadDomain.indexOf('googleapis') !== -1) ? '' : '/') + filename;

        return `${downloadUrl}?uuid=${fileInfo.id}`;
    }

    /**
     * @param preview marks the read as a preview so the edge does NOT record a download.
     *
     * This URL points at `/downloads/<file>` -- the same endpoint the Download button
     * uses -- so previewing a result used to flip `workflow.downloaded = true`. That
     * inflates the download rate and would wreck it as a retention metric once the
     * preview becomes prominent. Previews are reads, not takes.
     */
    getRangeSupportedDirectDownloadLink(fileInfo: FileUpload, filename: string, preview = false) {
        const fileDownloadDomain = fileInfo.endpointData.processorMetadataList[0].downloadDomain;
        const downloadUrl = ApiConfig.suffixDomainWithHttpsViewEndpoint(fileDownloadDomain)
            +
            ((fileDownloadDomain.indexOf('googleapis') !== -1) ? '' : '/') + filename;

        return `${downloadUrl}?uuid=${fileInfo.id}${preview ? '&preview=true' : ''}`;
    }

    /**
     * Server-rendered poster for a converted output.
     *
     * The fallback for results this browser cannot decode itself -- HEIC, TIFF, BMP, an
     * AV1-coded MP4 -- where the alternative is showing the user nothing and letting them
     * conclude the conversion failed. The edge renders images, the first page of a PDF and a
     * video frame; audio and archives have no frame, so it 404s and the caller keeps its icon.
     *
     * Addressed through `/v2/edge` rather than the `/downloads/` path the Download button uses:
     * that path is served straight off disk by nginx (`try_files .../content/output/$1`) and
     * never reaches ironman, so a `/downloads/thumb/...` URL would just 404 on a missing file.
     *
     * Never records a download -- it is a different endpoint from the one that does.
     */
    getOutputThumbnailLink(fileInfo: FileUpload, filename: string): string | null {
        const domain = fileInfo?.endpointData?.processorMetadataList?.[0]?.downloadDomain;
        if (!domain || !filename || !fileInfo.id) return null;
        // GCS-hosted outputs are not served by an edge, so there is nothing to ask.
        if (domain.indexOf('googleapis') !== -1) return null;
        const base = ApiConfig.suffixDomainWithHttpsEdgeEndpoint(domain);
        return `${base}/downloads/thumb/${encodeURIComponent(filename)}?uuid=${encodeURIComponent(fileInfo.id)}`;
    }

    downloadFile(fileInfo: FileUpload, filename: string) {
        const downloadUrl = this.getDirectDownloadLink(fileInfo, filename);

        // if(fileDownloadDomain.indexOf('googleapis') !== -1){
        //     const commEndpoint = fileInfo.endpointData.processorMetadataList[0].commEndpoint;
        //     const commOutputExistEndpoint = fileDownloadDomain + commEndpoint + `/output-file-exists`;
        //     this.http.post(commOutputExistEndpoint, {
        //         filename
        //     });

        // }
        // July 18, 2020 - Disabled downloadng file though www.xconvert.com. Instead navigate user to file URL
        // const openedWindow: Window = window.open('/download?url=' + encodeURIComponent(downloadUrl));
        const openedWindow: Window = window.open(downloadUrl);
        if (downloadUrl.endsWith('zip')) {
            this.gaService.send(
                GAContants.HitType.EVENT,
                GAContants.EventCategory.BUTTON,
                GAContants.EventAction['FILE-DOWNLOAD-ZIP'],
                GAContants.EventLabel.DOWNLOAD,
                fileInfo.name,
            );
        } else {
            this.gaService.send(
                GAContants.HitType.EVENT,
                GAContants.EventCategory.BUTTON,
                GAContants.EventAction['FILE-DOWNLOAD'],
                GAContants.EventLabel.DOWNLOAD,
                '',
            );
        }
        if (!openedWindow || openedWindow.closed || typeof openedWindow.closed === 'undefined') {
            return false;
        }
        return true;
    }

    downloadFileInputFile(fileInfo: FileUpload, localFilename: string) {
        const fileUploadDomain = fileInfo.endpointData.processorMetadataList[0].uploadDomain;
        const downloadUrl = ProcessorMetadataUtils.getUploadedFileDownloadEndpoint(
            fileInfo.endpointData.processorMetadataList[0]
        )
            + ((fileUploadDomain.indexOf('googleapis') !== -1) ? '' : '/') + localFilename;
        // const openedWindow: Window = window.open('/download?url=' + encodeURIComponent(downloadUrl));
        const openedWindow: Window = window.open(downloadUrl);
        if (downloadUrl.endsWith('zip')) {
            this.gaService.send(
                GAContants.HitType.EVENT,
                GAContants.EventCategory.BUTTON,
                GAContants.EventAction['FILE-DOWNLOAD-ZIP'],
                GAContants.EventLabel.DOWNLOAD,
                fileInfo.name,
            );
        } else {
            this.gaService.send(
                GAContants.HitType.EVENT,
                GAContants.EventCategory.BUTTON,
                GAContants.EventAction['FILE-DOWNLOAD'],
                GAContants.EventLabel.DOWNLOAD,
                '',
            );
        }
        if (!openedWindow || openedWindow.closed || typeof openedWindow.closed === 'undefined') {
            return false;
        }
        return true;
    }

    downloadGCSTempFile(fileInfo: FileUpload) {
        const domain = fileInfo.endpointData.processorMetadataList[0].processDomain || fileInfo.endpointData.processorMetadataList[0].uploadDomain;
        const downloadUrl = domain
            + fileInfo.endpointData.processorMetadataList[0].gcpTempDownloadEndpoint + '/' +
            fileInfo.endpointData.processorMetadataList[0].fileMetadata.gcName;
        // const _window: Window = window.open('/download?url=' + encodeURIComponent(downloadUrl));
        // const openedWindow = '/download?url=' + encodeURIComponent(downloadUrl);
        // window.open(openedWindow);
        const openedWindow: Window = window.open(downloadUrl);

        this.logger.trace7(downloadUrl);
        if (downloadUrl.endsWith('zip')) {
            this.gaService.send(
                GAContants.HitType.EVENT,
                GAContants.EventCategory.BUTTON,
                GAContants.EventAction['FILE-DOWNLOAD-ZIP'],
                GAContants.EventLabel.DOWNLOAD,
                fileInfo.name,
            );
        } else {
            this.gaService.send(
                GAContants.HitType.EVENT,
                GAContants.EventCategory.BUTTON,
                GAContants.EventAction['FILE-DOWNLOAD'],
                GAContants.EventLabel.DOWNLOAD,
                '',
            );
        }
    }

    getDownloadURL(fileInfo: FileUpload, filename: string) {
        const domain = fileInfo.endpointData.processorMetadataList[0].downloadDomain;
        const downloadUrl = ProcessorMetadataUtils.getDownloadEndpoint(fileInfo.endpointData.processorMetadataList[0]) +
            ((domain.indexOf('googleapis') !== -1) ? '' : '/') + filename;
        return `${downloadUrl}?uuid=${fileInfo.id}`;
    }

    /**
     * Not used and not tested
     * Google stored files are not handled
     * @param fileInfo 
     * @param filename 
     * @returns 
     */
    getDownloadURLWithFilenameAsAQueryParam(fileInfo: FileUpload, filename: string) {
        const domain = fileInfo.endpointData.processorMetadataList[0].downloadDomain;
        const downloadUrl = ProcessorMetadataUtils.getDownloadEndpoint(fileInfo.endpointData.processorMetadataList[0]) +
            ((domain.indexOf('googleapis') !== -1) ? '' : '/?file_name=') + filename;
        return downloadUrl;
    }
}
